import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { HomePageState } from '../../../containers/home/states/HomePageState';
import { groupByDate, Reservable, ReservableType } from '../../../../commonv3/types/reservable';
import { DateGroup } from '../../molecules/reservable/DateGroup';
import { ScheduleListItem } from '../../molecules/reservable/ScheduleListItem';
import { Button } from '../../../../commonv3/components/atoms/buttons/Button';
import { Confirmation } from '../../molecules/modals/Confirmation';
import { SettingPane } from '../../molecules/mypage/page';
import { GlobalStatesV3 } from '../../../../commonv3/states/GlobalStatesV3';
import { V3Routes } from '../../../routes';
import { LoaderRow } from '../../atoms/layout/LoaderRow';
import { TrialLessonCancellationPolicy } from '../../molecules/reservations/TrialLessonCancellationPolicy';
import { TrialLessonCancelConfirmation } from '../../molecules/modals/TrialLessonCancelConfirmation';
import { ReservationListState } from '../home/states/ReservationListState';
import { TokenType } from '../../../../commonv3/types/tokens';
import { MulticreatorScheduleListItem } from '../../molecules/reservable/MulticreatorScheduleListItem';
import { ReservationCancelSurveyShowPage } from '../../../containers/surveys/ReservationCancelSurveyPage';
import { OverlayPage } from '../../../../commonv3/components/molecules/layout/OverlayPage';
import { ReservationCancelCompletePane } from './ReservationCancelCompletePane';
import { CancelStep } from '../../../../commonv3/types/cancel';
import { AllReservationIndexView } from '../../molecules/reservations/ReservationCardList';
import moment from 'moment';
import { ToolTipTiny } from '../../atoms/tooltips/ToolTipTiny';
import { useState } from 'react';
import { FlagKeys } from '../../../../mainv2/states/CurrentUserState';
import { TrialLessonIndexView } from '../../../types/trialLessons';

type DialogProps = {
  reservationList: ReservationListState;
};

export function isMulticreatorSession(session: Reservable) {
  return session.required_token_type === TokenType.MULTICREATOR;
}

const CancelDialogs = observer(({ reservationList }: DialogProps) => {
  const {
    cancelDialog,
    confirmCancel,
    processingState,
    requestRescheduleTrialReservation,
    cancellingReservation,
    cancelStep,
    onRequestClose,
    onPrimaryButtonClick,
  } = reservationList;
  if (!cancellingReservation) {
    return null;
  }

  switch (cancelStep) {
    case CancelStep.CONFIRMATION:
      return cancellingReservation.reservable_type === ReservableType.TRIAL_LESSON ? (
        <TrialLessonCancelConfirmation
          headerMessage="本当にキャンセルしますか？"
          message={
            <TrialLessonCancellationPolicy
              showRescheduleInformation={true}
              showMypageLink={false}
              lesson={cancellingReservation.reservable as TrialLessonIndexView}
            />
          }
          rescheduleButtonText={
            cancellingReservation.reservable.location ? 'WEB体験に振り替える' : '他の枠に振り替える'
          }
          open={cancelDialog.isOpen}
          onReschedule={requestRescheduleTrialReservation}
          onAbort={cancelDialog.close}
          onConfirmation={confirmCancel}
        />
      ) : (
        <Confirmation
          id="cancel-confirmation"
          headerMessage="本当にキャンセルしますか？"
          message={'この操作は取り消しできません。'}
          confirmationText="キャンセルする"
          open={cancelDialog.isOpen}
          onAbort={cancelDialog.close}
          onConfirmation={confirmCancel}
          loading={processingState.submitting}
        />
      );
    case CancelStep.FORM:
      return (
        <OverlayPage id="cancel-form" open={cancelDialog.isOpen} onRequestClose={cancelDialog.close}>
          <ReservationCancelSurveyShowPage
            reservationId={reservationList.cancellingReservationId}
            reservationListState={reservationList}
          />
        </OverlayPage>
      );
    case CancelStep.COMPLETE:
      switch (cancellingReservation.reservable.required_token_type) {
        case TokenType.MOKUMOKU:
          const mokumokuRoute = V3Routes.mokumokuSessions2(
            cancellingReservation.reservable.membership_plan_group_keys[0],
            'mokumoku_rebook'
          );
          return (
            <ReservationCancelCompletePane
              onPrimaryButtonClick={() => onPrimaryButtonClick(mokumokuRoute)}
              buttonText="別の日程を予約する"
              onRequestClose={onRequestClose}
              isShowFormThanksMessage={false}
            />
          );
        case TokenType.GROUP_COACHING:
          const coachingRoute = V3Routes.coachingSessions2('coaching_rebook');
          return (
            <ReservationCancelCompletePane
              onPrimaryButtonClick={() => onPrimaryButtonClick(coachingRoute)}
              buttonText="別の日程を予約する"
              onRequestClose={onRequestClose}
              isShowFormThanksMessage={false}
            />
          );
        default:
          return (
            <ReservationCancelCompletePane
              onPrimaryButtonClick={() => onPrimaryButtonClick(V3Routes.home())}
              buttonText="マイページへ戻る"
              onRequestClose={onRequestClose}
              isShowFormThanksMessage={cancellingReservation.reservable_type === 'TrialLesson'}
            />
          );
      }
  }

  return null;
});

type CancelButtonProps = {
  reservation: AllReservationIndexView;
  onClick: () => void;
};
const CancelButton = ({ reservation, onClick }: CancelButtonProps) => {
  // TODO: 削除 she-4766
  const showSubmissionPlaceUrl = GlobalStatesV3.currentUser.hasEnabledFlagKey(
    FlagKeys.CANCELLATION_PERIOD_FOR_RESERVATION
  );
  const accountOnly = !GlobalStatesV3.currentUser.currentUser.v3_member;

  const cancellationLimit = moment(reservation.reservable.starts_at)
    .add(-1, 'day')
    .set('hour', 18)
    .set('minute', 0)
    .set('second', 0);

  const disabled = !accountOnly && showSubmissionPlaceUrl && moment() > cancellationLimit;

  const [isOpen, toggle] = useState<boolean>(false);

  return (
    <ToolTipTiny
      id={reservation.reservable.id.toString()}
      description="前日18時以降のキャンセルはできません"
      position="bottom"
      nowrap={true}
      horizontalOffset={-50}
      opened={isOpen}
    >
      <Button
        type="normal"
        size="m"
        disabled={disabled}
        onClick={() => onClick()}
        onMouseOver={() => disabled && toggle(true)}
        onMouseOut={() => disabled && toggle(false)}
        id={`cancel-button-${reservation.reservable_type}-${reservation.reservable.id}`}
      >
        キャンセル
      </Button>
    </ToolTipTiny>
  );
};

type Props = {
  homeState: HomePageState;
};

export const ReservationListPane = inject('homeState')(
  observer(
    class ReservationListPane extends React.Component<Props> {
      render() {
        const { reservationList } = this.props.homeState;
        const { loading, allReservationList, requestCancel } = reservationList;
        const groups = groupByDate(allReservationList, (r) => r.reservable.starts_at);

        return (
          <SettingPane activeMenu="reservations" title="予約一覧" id="reservation-list-pane">
            {loading && <LoaderRow />}
            {groups.map((g) => {
              return (
                <DateGroup key={g.date} date={g.date}>
                  {g.entries.map((entry) => {
                    if (isMulticreatorSession(entry.reservable)) {
                      return (
                        <MulticreatorScheduleListItem
                          id={`reservation-item-${entry.reservable_type}-${entry.reservable.id}`}
                          key={entry.id}
                          title={entry.reservable.title}
                          reservable={entry.reservable}
                        />
                      );
                    } else {
                      return (
                        <ScheduleListItem
                          id={`reservation-item-${entry.reservable_type}-${entry.reservable.id}`}
                          key={entry.id}
                          title={entry.reservable.title}
                          reservable={entry.reservable}
                          showZoomLink={true}
                          actionItem={<CancelButton reservation={entry} onClick={() => requestCancel(entry)} />}
                        />
                      );
                    }
                  })}
                </DateGroup>
              );
            })}
            <CancelDialogs reservationList={reservationList} />
          </SettingPane>
        );
      }
    }
  )
);
